import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PortalDsBadge } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  formatRequestDate,
  formatRequestTime,
  formatSchedulingRequestStatusLabel,
  getSchedulingRequestBadgeVariant,
} from '../utils/scheduling.mappers';
import type { SchedulingRequest } from '../types/scheduling.types';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface RequestsCalendarProps {
  visibleMonth: Date;
  requestsByDay: Record<string, SchedulingRequest[]>;
  selectedDay: string | null;
  onSelectDay: (dayKey: string) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
}

function buildCalendarCells(visibleMonth: Date) {
  const monthStart = dayjs(visibleMonth).startOf('month');
  const startOffset = (monthStart.day() + 6) % 7;
  const gridStart = monthStart.subtract(startOffset, 'day');

  return Array.from({ length: 42 }, (_, index) => {
    const date = gridStart.add(index, 'day');
    return {
      date,
      inMonth: date.month() === monthStart.month(),
    };
  });
}

export function RequestsCalendar({
  visibleMonth,
  requestsByDay,
  selectedDay,
  onSelectDay,
  onPreviousMonth,
  onNextMonth,
}: RequestsCalendarProps) {
  const cells = buildCalendarCells(visibleMonth);
  const monthLabel = dayjs(visibleMonth).format('MMMM YYYY');
  const hasRequests = Object.keys(requestsByDay).length > 0;
  const selectedDayRequests = selectedDay ? requestsByDay[selectedDay] ?? [] : [];

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="rounded-xl border border-[#EDEEF2] bg-white p-4 shadow-[0_1px_2px_rgba(13,15,26,0.05)]">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-[#0D0F1A]">
            {monthLabel}
          </h3>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous month"
              onClick={onPreviousMonth}
              className="inline-flex size-8 items-center justify-center rounded-lg border border-[#D9DBE3] text-[#5E6278] transition-colors hover:bg-[#F7F8FA]"
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Next month"
              onClick={onNextMonth}
              className="inline-flex size-8 items-center justify-center rounded-lg border border-[#D9DBE3] text-[#5E6278] transition-colors hover:bg-[#F7F8FA]"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className="px-1 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]"
            >
              {label}
            </div>
          ))}

          {cells.map((cell) => {
            const dayKey = cell.date.format('YYYY-MM-DD');
            const dayRequests = requestsByDay[dayKey] ?? [];
            const isSelected = selectedDay === dayKey;

            return (
              <button
                key={dayKey}
                type="button"
                aria-label={
                  dayRequests.length > 0
                    ? `${cell.date.format('D MMMM YYYY')}, ${dayRequests.length} meeting request${dayRequests.length === 1 ? '' : 's'}`
                    : cell.date.format('D MMMM YYYY')
                }
                onClick={() => onSelectDay(dayKey)}
                className={cn(
                  'flex min-h-[72px] flex-col rounded-lg border p-2 text-left transition-colors',
                  cell.inMonth ? 'border-[#EDEEF2] bg-white hover:bg-[#F7F8FA]' : 'border-transparent bg-[#FAFAFB] text-[#C4C7D1]',
                  isSelected && 'border-[#0D0F1A] bg-[#F7F8FA] ring-1 ring-[#0D0F1A]'
                )}
              >
                <span className={cn('text-[12px] font-medium', cell.inMonth ? 'text-[#0D0F1A]' : 'text-[#C4C7D1]')}>
                  {cell.date.date()}
                </span>
                {dayRequests.length > 0 ? (
                  <span className="mt-auto inline-flex items-center gap-1 text-[11px] font-semibold text-[#2563EB]">
                    <span className="size-1.5 rounded-full bg-[#2563EB]" />
                    {dayRequests.length}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {!hasRequests ? (
          <p className="mt-4 text-center text-[13px] text-[#9CA3AF]">
            No meeting requests scheduled this month.
          </p>
        ) : null}
      </div>

      <div className="rounded-xl border border-[#EDEEF2] bg-white p-4 shadow-[0_1px_2px_rgba(13,15,26,0.05)]">
        <h3 className="text-[15px] font-semibold text-[#0D0F1A]">
          Day details
        </h3>
        {!selectedDay ? (
          <p className="mt-3 text-[13px] text-[#9CA3AF]">
            Select a day to view meeting requests.
          </p>
        ) : selectedDayRequests.length === 0 ? (
          <p className="mt-3 text-[13px] text-[#9CA3AF]">
            No meeting requests on
            {' '}
            {formatRequestDate(`${selectedDay}T12:00:00`)}
            .
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            <p className="text-[13px] text-[#5E6278]">
              {formatRequestDate(selectedDayRequests[0].scheduled_at)}
            </p>
            {selectedDayRequests.map((request) => (
              <div
                key={request.id}
                className="rounded-lg border border-[#EDEEF2] p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[13px] font-semibold text-[#0D0F1A]">
                      {formatRequestTime(request.scheduled_at)}
                    </p>
                    <p className="mt-1 text-[13px] text-[#0D0F1A]">
                      {request.client_name}
                    </p>
                  </div>
                  <PortalDsBadge
                    variant={getSchedulingRequestBadgeVariant(request.status)}
                    pill
                  >
                    {formatSchedulingRequestStatusLabel(request.status)}
                  </PortalDsBadge>
                </div>
                <p className="mt-2 text-[12px] text-[#5E6278]">
                  Teammate:
                  {' '}
                  {request.teammate_name}
                </p>
                <p className="text-[12px] text-[#5E6278]">
                  Attendee:
                  {' '}
                  {request.attendee_email}
                </p>
                {request.google_meet_link ? (
                  <p className="mt-2 text-[12px] text-[#5E6278]">
                    Meet:
                    {' '}
                    <a
                      href={request.google_meet_link}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-[#4F46E5] underline"
                    >
                      {request.google_meet_link}
                    </a>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
